import { useState } from 'react';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  getGetInterviewTypeQueryKey,
  getGetInterviewTypeQueryOptions,
  getListInterviewTypesQueryKey,
  useArchiveInterviewType,
  useUnarchiveInterviewType,
} from '@sara/api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@sara/ui/components/alert-dialog';
import { buttonVariants } from '@sara/ui/components/button';
import { useDialog } from '@sara/ui/hooks/use-dialog';
import { toast } from '@sara/ui/components/sonner';
import { cn } from '@sara/ui/lib/utils';
import { InterviewTypeDetail } from '../../components/InterviewTypeDetail';
import { EditInterviewTypeDialog } from '../EditInterviewTypeDialog';
import { toSuspense } from '../../../../lib/query';
import { useOrgSlug } from '../../../auth';
import type { InterviewTypeDetailContainerProps } from './types';

type PendingAction = 'archive' | 'unarchive' | null;

export function InterviewTypeDetailContainerContent({
  interviewTypeId,
}: InterviewTypeDetailContainerProps) {
  const { data } = useSuspenseQuery(toSuspense(getGetInterviewTypeQueryOptions(interviewTypeId)));
  const navigate = useNavigate();
  const orgSlug = useOrgSlug();

  const queryClient = useQueryClient();
  const editDialog = useDialog(EditInterviewTypeDialog);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetInterviewTypeQueryKey(data.id) });
    void queryClient.invalidateQueries({ queryKey: getListInterviewTypesQueryKey() });
  };

  const archive = useArchiveInterviewType({
    mutation: {
      onSuccess: () => {
        toast.success('Interview type archived');
        setPendingAction(null);
        invalidate();
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to archive interview type');
      },
    },
  });
  const unarchive = useUnarchiveInterviewType({
    mutation: {
      onSuccess: () => {
        toast.success('Interview type restored');
        setPendingAction(null);
        invalidate();
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to restore interview type');
      },
    },
  });

  const isMutating = archive.isPending || unarchive.isPending;
  const isArchiving = pendingAction === 'archive';

  return (
    <>
      <InterviewTypeDetail
        interviewType={data}
        orgSlug={orgSlug}
        onCreateVersion={() =>
          navigate({
            to: '/orgs/$orgSlug/templates/$interviewTypeId/edit',
            params: { orgSlug, interviewTypeId: data.id },
          })
        }
        onEdit={() => editDialog.open({ interviewType: data })}
        onArchive={() => setPendingAction('archive')}
        onUnarchive={() => setPendingAction('unarchive')}
        isArchivePending={isMutating}
      />

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open && !isMutating) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArchiving ? 'Archive this interview type?' : 'Restore this interview type?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArchiving
                ? "It will remain visible but you won't be able to use it for new invites."
                : 'It will be usable for new invites again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(isArchiving && buttonVariants({ variant: 'destructive' }))}
              disabled={isMutating}
              onClick={(event) => {
                event.preventDefault();
                if (isArchiving) {
                  archive.mutate({ id: data.id });
                } else {
                  unarchive.mutate({ id: data.id });
                }
              }}
            >
              {isArchiving
                ? archive.isPending
                  ? 'Archiving…'
                  : 'Archive'
                : unarchive.isPending
                  ? 'Restoring…'
                  : 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
